import api from './axios';
import { Category } from '../types/category';

export interface Collection {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  urlPath: string;
  brandId: number | null;
  collectionType: string;
  onlineStore: boolean;
  pointOfSale: boolean;
  themeTemplate: string;
  searchEngineTitle: string;
  searchEngineDescription: string;
  createdAt: string;
  updatedAt: string;
  isPublishedOnline: boolean;
}

export interface CollectionProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  comparePrice?: number | null;
  imageUrl: string;
  imageGallery: string[];
  brand?: string;
  vendor?: string;
  brandId?: number;
  nameZh?: string | null;
  nameCn?: string | null;
  descriptionZh?: string | null;
  descriptionCn?: string | null;
  sku?: string;
  barcode?: string | null;
  costPerItem?: number | null;
  stockAvailable?: number;
  stockReserved?: number;
  stockSold?: number;
  trackQuantity?: boolean;
  chargeTax?: boolean;
  continueSelling?: boolean;
  type?: string | null;
  collections?: unknown;
  tags?: string | null;
  variantOptions?: unknown[];
  variants?: unknown[];
  isPhysicalProduct?: boolean;
  weight?: number | null;
  weightUnit?: string;
  isActive?: boolean;
  ourPick?: boolean;
  eat365ProductId?: string | null;
  createdAt?: string;
  category?: string;
  categories?: Category[];
  inStock?: boolean;
  quantity?: number;
}

export interface PaginatedProductsResponse {
  products: CollectionProduct[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetch collection data by URL path
 * @param urlPath - The URL path of the collection (e.g., "test-collection")
 * @returns Promise<Collection>
 */
export const getCollectionByUrl = async (urlPath: string): Promise<Collection> => {
  const response = await api.get(`/api/collections/by-url/${urlPath}`);
  return response.data;
};

/**
 * Fetch products for a specific collection
 * @param collectionId - The ID of the collection
 * @returns Promise<CollectionProduct[]>
 */
export const getProductsByCollection = async (collectionId: number): Promise<CollectionProduct[]> => {
  const response = await api.get(`/api/products?collectionId=${collectionId}`);
  
  // Handle both paginated and direct array responses
  if (response.data && typeof response.data === 'object' && 'products' in response.data) {
    // Paginated response
    return response.data.products;
  } else {
    // Direct array response
    return response.data;
  }
};

/**
 * Fetch paginated products for a collection
 * @param collectionId - The ID of the collection
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @returns Promise<PaginatedProductsResponse>
 */
export const getPaginatedProductsByCollection = async (
  collectionId: number, 
  page: number = 1, 
  limit: number = 20
): Promise<PaginatedProductsResponse> => {
  const response = await api.get(`/api/products?collectionId=${collectionId}&page=${page}&limit=${limit}`);
  return response.data;
};
